import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'

import 'dayjs/locale/en'
import 'dayjs/locale/pt'

dayjs.extend(customParseFormat)
dayjs.extend(localeData)
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(utc)

export function setDayjsLocale(language: 'pt' | 'en'): void {
  dayjs.locale(language === 'pt' ? 'pt' : 'en')
}

export function formatDate(
  value: string | Date | null | undefined,
  fmt = 'L',
): string {
  if (!value) return '—'
  const parsed = dayjs(value)
  if (!parsed.isValid()) return '—'
  return parsed.format(fmt)
}

export function formatDateTime(
  value: string | Date | null | undefined,
  fmt = 'L LT',
): string {
  return formatDate(value, fmt)
}

/**
 * Returns a localized time-of-day (e.g., "16:30"). Uses dayjs's `LT` token
 * which respects the active locale.
 */
export function formatTime(
  value: string | Date | null | undefined,
  fmt = 'LT',
): string {
  return formatDate(value, fmt)
}

export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const parsed = dayjs(value)
  if (!parsed.isValid()) return '—'
  return parsed.fromNow()
}

/**
 * Returns a short weekday label like "Sat", using the current locale.
 */
export function formatWeekday(value: string | Date | null | undefined): string {
  if (!value) return ''
  const parsed = dayjs(value)
  if (!parsed.isValid()) return ''
  return parsed.format('ddd')
}

/**
 * Returns a full day heading like "Saturday, 12 April".
 */
export function formatDayHeading(
  value: string | Date | null | undefined,
): string {
  if (!value) return '—'
  const parsed = dayjs(value)
  if (!parsed.isValid()) return '—'
  return parsed.format('dddd, D MMMM')
}

/**
 * Bucket a list of items by the YYYY-MM-DD key of a date accessor.
 * Order is preserved from the input.
 */
export function groupByDay<T>(
  items: T[],
  getDate: (item: T) => string | Date | null | undefined,
): Array<{ day: string; items: T[] }> {
  const order: string[] = []
  const buckets = new Map<string, T[]>()
  for (const item of items) {
    const raw = getDate(item)
    const key = raw ? dayjs(raw).format('YYYY-MM-DD') : 'unknown'
    const list = buckets.get(key)
    if (list) {
      list.push(item)
      continue
    }
    buckets.set(key, [item])
    order.push(key)
  }
  return order.map((day) => ({ day, items: buckets.get(day)! }))
}

/**
 * Format a duration (ms) like "1m 20s" or "5s". Returns '—' for non-positive.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—'
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remSeconds = seconds % 60
  if (minutes < 60) {
    return remSeconds ? `${minutes}m ${remSeconds}s` : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remMinutes = minutes % 60
  return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`
}

/**
 * Returns durationMs between two ISO strings, or null if either is missing.
 */
export function diffMs(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
): number | null {
  if (!start || !end) return null
  const a = dayjs(start)
  const b = dayjs(end)
  if (!a.isValid() || !b.isValid()) return null
  return b.diff(a)
}
